import React from 'react';
import { Badge } from '../../../../../components/badge';
import type { SurveySummaryQuestion } from '../../types/survey.types';

interface MultipleChoiceGridResponseProps {
    question: SurveySummaryQuestion;
    totalResponses: number;
}

const MultipleChoiceGridResponse: React.FC<MultipleChoiceGridResponseProps> = ({ 
    question, 
    totalResponses 
}) => {
    const { position, title, answered_count, options } = question;
    
    // Calculate response rate
    const responseRate = totalResponses > 0 ? ((answered_count / totalResponses) * 100).toFixed(1) : '0.0';
    
    // Parse grid data from options
    // Assuming options contain grid data in format: "row_text|column_text|count"
    const gridData = parseGridData(options, answered_count);
    
    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-600 p-6 shadow-sm">
            {/* Header Section */}
            <div className="flex items-start gap-3 mb-6">
                {/* Question Icon */}
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 bg-[#F2FCFA] dark:bg-emerald-900/30">
                    <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16.4329 2.90967H4.76628C3.8458 2.90967 3.09961 3.65586 3.09961 4.57633V16.243C3.09961 17.1635 3.8458 17.9097 4.76628 17.9097H16.4329C17.3534 17.9097 18.0996 17.1635 18.0996 16.243V4.57633C18.0996 3.65586 17.3534 2.90967 16.4329 2.90967Z" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M3.09961 7.90967H18.0996" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M3.09961 12.9097H18.0996" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8.09961 2.90967V17.9097" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M13.0996 2.90967V17.9097" stroke="#00C49A" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </div>
                
                <div className="flex-1">
                    {/* Question Title */}
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {position}. {title}
                    </h3>
                    
                    {/* Question Type Badge and Response Info */}
                    <div className="flex items-center gap-4 flex-wrap">
                        <Badge 
                            variant="outline" 
                            className="text-[10px] text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-800"
                        >
                            Multiple Choice Grid
                        </Badge>
                        
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                            <span className="font-medium">{answered_count}</span> of <span className="font-medium">{totalResponses}</span> responses
                        </div>
                        
                        <div className="text-sm font-medium text-[#004232] dark:text-[#00D6A8]" >
                            {responseRate}% response rate
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Grid Table */}
            {gridData.rows.length > 0 && gridData.columns.length > 0 ? (
                <div className="overflow-hidden bg-white dark:bg-gray-700 rounded-lg">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b dark:border-gray-500">
                                <th className="px-4 py-3 text-left font-bold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700">
                                    Question
                                </th>
                                {gridData.columns.map((column) => (
                                    <th key={column} className="px-4 py-3 text-center font-bold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700">
                                        {column}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {gridData.rows.map((row, rowIndex) => (
                                <tr key={row} className="border-t dark:border-gray-500">
                                    <td className="px-4 py-3 font-bold text-gray-700 dark:text-gray-200">
                                        {row}
                                    </td>
                                    {gridData.columns.map((column, colIndex) => {
                                        const cellData = gridData.data[row]?.[column] || { count: 0, percentage: 0 };
                                        return (
                                            <td key={column} className="px-4 py-3 text-center">
                                                <div className="flex flex-col items-center gap-1">
                                                    {/* Count with pill background */}
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200">
                                                        {cellData.count}
                                                    </span>
                                                    {/* Percentage */}
                                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                                        {cellData.percentage}%
                                                    </span>
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No grid data available for this question
                </div>
            )}
        </div>
    );
};

// Helper function to parse grid data from options
function parseGridData(options: any[], answered_count: number) {
    const rows = new Set<string>();
    const columns = new Set<string>();
    const data: Record<string, Record<string, { count: number; percentage: number }>> = {};
    
    // Parse options to extract grid structure
    options.forEach(option => {
        // Try different formats for grid data
        let row = '';
        let column = '';
        
        // Format 1: "row|column" 
        if (option.label.includes('|')) {
            const parts = option.label.split('|');
            if (parts.length >= 2) {
                row = parts[0].trim();
                column = parts[1].trim();
            }
        }
        // Format 2: "row - column" or "row: column"
        else if (option.label.includes(' - ') || option.label.includes(': ')) {
            const separator = option.label.includes(' - ') ? ' - ' : ': ';
            const parts = option.label.split(separator);
            if (parts.length >= 2) {
                row = parts[0].trim();
                column = parts[1].trim();
            }
        }
        // Format 3: Check if option_id contains grid info
        else if (option.option_id && option.option_id.includes('_')) {
            const parts = option.option_id.split('_');
            if (parts.length >= 2) {
                row = parts[0];
                column = parts[1];
            }
        }
        // Fallback: treat as single column with row as label
        else {
            row = option.label;
            column = 'Response';
        }
        
        if (row && column) {
            rows.add(row);
            columns.add(column);
            
            if (!data[row]) {
                data[row] = {};
            }
            
            const percentage = answered_count > 0 ? ((option.count / answered_count) * 100).toFixed(0) : '0';
            data[row][column] = {
                count: option.count,
                percentage: parseFloat(percentage)
            };
        }
    });
    
    return {
        rows: Array.from(rows).sort(),
        columns: Array.from(columns).sort(),
        data
    };
}

export default MultipleChoiceGridResponse;
